// =============================================================================
// MeuDriver — usePushNotifications
//
// Hook para gerenciar push notifications PWA via VAPID.
//
// Uso:
//   const { isSupported, permission, subscribe, unsubscribe } = usePushNotifications()
//
// Dependências externas: nenhuma nova. Usa supabase client existente.
// VAPID_PUBLIC_KEY deve estar em VITE_VAPID_PUBLIC_KEY no .env do front.
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
// Quando rodando em container Capacitor nativo, o registro de push é feito pelo
// plugin @capacitor/push-notifications — não pelo service worker VAPID.
// Portanto, pulamos a lógica web se isNative() retornar true.
import { isNative } from "@/lib/capacitor";

// A chave pública VAPID é segura para expor no front-end
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

/** Converte uma string base64url para Uint8Array (necessário para applicationServerKey). */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Removido: `arrayBufferToBase64Url`. Era aplicado sobre
// `subscription.toJSON().keys`, que já é `Record<string, string>` em base64url
// — passar uma string para `new Uint8Array(...)` produzia um array vazio, então
// p256dh e auth eram gravados em branco e as notificações push nunca podiam ser
// cifradas. Agora os valores do browser são usados diretamente.

export type PushPermission = "default" | "granted" | "denied";

export interface UsePushNotificationsReturn {
  /** Se o browser suporta Push API e Service Worker. */
  isSupported: boolean;
  /** Estado atual da permissão de notificação do browser. */
  permission: PushPermission;
  /** Solicita permissão e registra a subscription no banco. */
  subscribe: () => Promise<void>;
  /** Remove a subscription do banco e cancela no PushManager. */
  unsubscribe: () => Promise<void>;
  /** true enquanto subscribe/unsubscribe estão em andamento. */
  isLoading: boolean;
}

export function usePushNotifications(): UsePushNotificationsReturn {
  const { user } = useAuth();

  // Em ambiente Capacitor nativo, o serviço de push é gerenciado pelo plugin
  // @capacitor/push-notifications — não pela Push API do browser. Nesse caso,
  // reportamos isSupported=false para que o componente não exiba o toggle web.
  const isSupported =
    !isNative() &&
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

  const [permission, setPermission] = useState<PushPermission>(
    isSupported ? (Notification.permission as PushPermission) : "denied",
  );
  const [isLoading, setIsLoading] = useState(false);

  // Sincroniza com o estado real da permissão sempre que muda
  useEffect(() => {
    if (!isSupported) return;
    setPermission(Notification.permission as PushPermission);
  }, [isSupported]);

  const subscribe = useCallback(async () => {
    if (!isSupported) {
      toast.error("Seu navegador não suporta notificações push.");
      return;
    }
    if (!user?.id) {
      toast.error("Você precisa estar logado para ativar notificações.");
      return;
    }
    if (!VAPID_PUBLIC_KEY) {
      // Em dev, avisa mas não bloqueia totalmente (pode não estar configurado)
      console.warn("[usePushNotifications] VITE_VAPID_PUBLIC_KEY não configurado.");
      toast.error("Configuração de notificações incompleta. Contate o suporte.");
      return;
    }

    setIsLoading(true);
    try {
      // 1. Solicita permissão ao usuário
      const result = await Notification.requestPermission();
      setPermission(result as PushPermission);

      if (result !== "granted") {
        toast.error("Permissão de notificações negada.");
        return;
      }

      // 2. Registra ou recupera o Service Worker
      const registration = await navigator.serviceWorker.ready;

      // 3. Cria a subscription no PushManager
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        // `BufferSource` não aceita `Uint8Array<ArrayBufferLike>` direto no
        // lib DOM do TS 5.9; o buffer subjacente é um ArrayBuffer comum.
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
          .buffer as ArrayBuffer,
      });

      // `PushSubscriptionJSON.keys` é `Record<string, string>` (base64url),
      // não ArrayBuffer — os valores já vêm codificados pelo browser.
      const subscriptionJson = subscription.toJSON();
      const endpoint = subscriptionJson.endpoint!;
      const keys = subscriptionJson.keys ?? {};
      const p256dh = keys.p256dh ?? "";
      const auth = keys.auth ?? "";

      // 4. Persiste no banco
      const { error } = await supabase.from("push_subscriptions").upsert(
        { user_id: user.id, endpoint, p256dh, auth },
        { onConflict: "user_id,endpoint" },
      );

      if (error) throw new Error(error.message);

      toast.success("Notificações ativadas!");
    } catch (err) {
      console.error("[usePushNotifications] subscribe error:", err);
      toast.error("Erro ao ativar notificações. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, user?.id]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported || !user?.id) return;

    setIsLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        const endpoint = subscription.endpoint;

        // Remove do banco primeiro
        await supabase
          .from("push_subscriptions")
          .delete()
          .eq("user_id", user.id)
          .eq("endpoint", endpoint);

        // Cancela no browser
        await subscription.unsubscribe();
      }

      setPermission("default");
      toast.success("Notificações desativadas.");
    } catch (err) {
      console.error("[usePushNotifications] unsubscribe error:", err);
      toast.error("Erro ao desativar notificações.");
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, user?.id]);

  return { isSupported, permission, subscribe, unsubscribe, isLoading };
}
